// Settings (admin only) and the clock. The sending window and the 7-day gap are rules every campaign obeys
// (SPEC §8, C5); the clock answer tells every screen whether the window is open right now.
import { z } from 'zod'
import { IsoDate, IsoDateTime } from './common'
import { TwoStepRule } from './session'

export const Settings = z.object({
  send_from: z
    .number('Type an hour, 0 to 23.')
    .int()
    .min(0, 'Type an hour, 0 to 23.')
    .max(23, 'Type an hour, 0 to 23.')
    .describe('first hour of the sending window, IST'),
  send_to: z
    .number('Type an hour, 1 to 24.')
    .int()
    .min(1, 'Type an hour, 1 to 24.')
    .max(24, 'Type an hour, 1 to 24.')
    .describe('the window closes at this hour, IST'),
  marketing_gap_days: z
    .number('Type a number of days.')
    .int()
    .min(1, 'The gap is at least 1 day.')
    .describe('one marketing message per person per N days'),
  cap_default: z
    .number('Type a number.')
    .int()
    .min(1, 'The cap is at least 1 a day.')
    .describe('default cap per number per day for a new campaign'),
  dnd_source: z.string().trim().min(1, 'Say where the DND list comes from.'),
  events_keep_months: z
    .number('Type a number of months, 6 to 36.')
    .int()
    .min(6, 'At least 6 months.')
    .max(36, 'At most 36 months.')
    .describe(
      'W5: how long the events (every message status, run step, click, lender call) are kept in ClickHouse; the oldest month is archived to Parquet first'
    ),
  webhooks_keep_days: z
    .number('Type a number of days, 7 to 365.')
    .int()
    .min(7, 'At least 7 days.')
    .max(365, 'At most 365 days.')
    .describe("WH1: how long each webhook line (a campaign's Webhooks tab and its CSV, Log → Webhooks) is kept"),
  meta_stop_percent: z
    .number('Type a number, 1 to 100.')
    .int()
    .min(1, 'Type a number, 1 to 100.')
    .max(100, 'Type a number, 1 to 100.')
    .describe(
      "the portal stops sending WhatsApp at this share of each WhatsApp account's limit (95 = 1,900 of 2,000); W1: the limit itself is on Numbers"
    ),
  two_step_rule: TwoStepRule.describe('optional: each person turns 2-step on; required: set up at sign-in'),
})
export type Settings = z.infer<typeof Settings>

export const SaveSettingsInput = Settings.refine((s) => s.send_from < s.send_to, {
  message: 'The window must start before it ends.',
  path: ['send_to'],
})
export type SaveSettingsInput = z.infer<typeof SaveSettingsInput>

export const Weekday = z.enum(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'])
export type Weekday = z.infer<typeof Weekday>

/** "Now" for the portal: the server's clock (the demo clock on fixtures). */
export const Clock = z.object({
  now: IsoDateTime,
  date: IsoDate,
  hour: z.number().int().min(0).max(23),
  weekday: Weekday,
  window_open: z.boolean(),
  send_from: z.number().int(),
  send_to: z.number().int(),
})
export type Clock = z.infer<typeof Clock>

export interface SettingsApi {
  getSettings(): Promise<Settings>
  saveSettings(input: SaveSettingsInput): Promise<Settings>
  getClock(): Promise<Clock>
}
